import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { downloadBlob } from "../lib/downloads";
import { ROLE_WORKSPACE, canManageGeneratedDocuments, getApplicableProjectRole, getVisibleProjectSectionTabs } from "../lib/roleWorkspace";
import { CaretLeft, ArrowsClockwise, Lock, Cpu, Plus } from "@phosphor-icons/react";
import ClassificationTab from "../components/tabs/ClassificationTab";
import TechnicalFileTab from "../components/tabs/TechnicalFileTab";
import RiskTab from "../components/tabs/RiskTab";
import ClinicalTab from "../components/tabs/ClinicalTab";
import AiActTab from "../components/tabs/AiActTab";
import GapsTab from "../components/tabs/GapsTab";
import SubmissionTab from "../components/tabs/SubmissionTab";
import AuditTab from "../components/tabs/AuditTab";
import GsprTab from "../components/tabs/GsprTab";
import NotifiedBodyTab from "../components/tabs/NotifiedBodyTab";
import CeMarkingTab from "../components/tabs/CeMarkingTab";
import PmsTab from "../components/tabs/PmsTab";
import QmsTab from "../components/tabs/QmsTab";
import TraceTab from "../components/tabs/TraceTab";
import DocumentsSection from "../components/DocumentsSection";
import { Empty } from "../components/tabs/OverviewTab";
import PRRCWidget from "../components/PRRCWidget";
import EudamedReadinessPanel from "../components/EudamedReadinessPanel";
import OperatorClassificationReview from "../components/OperatorClassificationReview";
import { buildEudamedReadinessItems } from "../lib/eudamedReadiness";
import { getProjectModule, getProjectSection, getProjectSectionForModule, getRegulatoryTargetId } from "../lib/projectNavigation";
import { useT } from "../lib/i18n";
import WorkspaceLoading from "../components/WorkspaceLoading";
import ProjectOverview from "../components/ProjectOverview";

const REGULATORY_TABS = [
  { value: "preparation", label: "tab.preparation", testid: "tab-preparation" },
  { value: "classification", label: "tab.classification", testid: "tab-classification" },
  { value: "risk", label: "tab.risk", testid: "tab-risk" },
  { value: "gspr", label: "tab.gspr", testid: "tab-gspr" },
  { value: "notified-body", label: "tab.notified_body", testid: "tab-notified-body" },
  { value: "operator-review", label: "operator_review.title", testid: "tab-operator-review" },
  { value: "ce-marking", label: "tab.ce_marking", testid: "tab-ce-marking" },
  { value: "techfile", label: "tab.techfile", testid: "tab-techfile" },
  { value: "clinical", label: "tab.clinical", testid: "tab-clinical" },
  { value: "aiact", label: "tab.aiact", testid: "tab-aiact" },
  { value: "submission", label: "tab.submission", testid: "tab-submission" },
  { value: "documents", label: "tab.documents", testid: "tab-documents" },
];

const POSTMARKET_TABS = [
  { value: "pms", label: "tab.pms", testid: "tab-pms" },
  { value: "pmcf", label: "tab.pmcf", testid: "tab-pmcf" },
  { value: "capa", label: "tab.capa", testid: "tab-gaps" },
  { value: "ncr", label: "tab.ncr", testid: "tab-ncr" },
  { value: "complaints", label: "tab.complaints", testid: "tab-complaints" },
  { value: "recall", label: "tab.recall", testid: "tab-recall" },
  { value: "fsca", label: "tab.fsca", testid: "tab-fsca" },
  { value: "trace", label: "tab.trace", testid: "tab-trace" },
  { value: "audit", label: "tab.audit", testid: "tab-audit" },
];

export default function ProjectDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get("section");
  const moduleParam = searchParams.get("module");
  const section = getProjectSection(sectionParam, moduleParam);
  const requestedDeviceId = searchParams.get("device_id") || "";
  const [project, setProject] = useState(null);
  const [projectLoadError, setProjectLoadError] = useState(false);
  const [devices, setDevices] = useState([]);
  const [active, setActive] = useState(null);
  const [activeLoading, setActiveLoading] = useState(true);
  const [activeLoadError, setActiveLoadError] = useState(false);
  const [versionRefresh, setVersionRefresh] = useState(0);
  const activeDevice = devices.find((device) => device.id === requestedDeviceId)
    || devices.find((device) => device.id === project?.device_id)
    || devices[0]
    || null;
  const activeDeviceId = activeDevice?.id || "";
  const [companyProfile, setCompanyProfile] = useState(null);
  const applicableRole = getApplicableProjectRole(companyProfile?.operating_role_view);
  const roleWorkspace = useMemo(() => ROLE_WORKSPACE[applicableRole] || {
    labelKey: "project.role_pending",
    regulatoryTabs: [],
    postmarketTabs: [],
    defaultRegulatoryTab: "preparation",
    defaultPostmarketTab: "trace",
  }, [applicableRole]);
  const visibleProjectSectionTabs = useMemo(
    () => getVisibleProjectSectionTabs(applicableRole, active?.ai_act?.applicable === true),
    [applicableRole, active?.ai_act?.applicable],
  );
  const [audit, setAudit] = useState([]);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const [attachmentsByDevice, setAttachmentsByDevice] = useState({});
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState(false);
  const [readinessRefresh, setReadinessRefresh] = useState(0);
  const [packageBusy, setPackageBusy] = useState(false);
  const [packageError, setPackageError] = useState("");
  const [exportError, setExportError] = useState("");
  const [downloadBusyId, setDownloadBusyId] = useState("");
  const [reanalyzing, setReanalyzing] = useState(false);
  const [rerunNotice, setRerunNotice] = useState("");
  const [rerunError, setRerunError] = useState("");
  const [activeProjectModule, setActiveProjectModule] = useState(() => getProjectModule(sectionParam, moduleParam));
  const [deviceListExpanded, setDeviceListExpanded] = useState(false);
  const [clinicalFocusRequest, setClinicalFocusRequest] = useState("");
  const clearClinicalFocusRequest = useCallback(() => setClinicalFocusRequest(""), []);
  const t = useT();
  const lang = localStorage.getItem("rg_lang") || "en";
  function roleSetupPrompt() {
    return (
      <div role="alert" className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-[var(--text)]" data-testid="global-role-required">
        <p>{t("pd.profile_required")}</p>
        <Link to="/company" className="mt-2 inline-block font-semibold text-[var(--accent-text)] underline underline-offset-2">
          {t("company.role_view.open_settings")}
        </Link>
      </div>
    );
  }

  const load = useCallback(async () => {
    setProjectLoadError(false);
    try {
      const [p, a, ds] = await Promise.all([
        api.get(`/projects/${pid}`),
        api.get(`/projects/${pid}/audit`),
        api.get(`/projects/${pid}/devices`),
      ]);
      setProject(p.data);
      setDevices(ds.data || []);
      setAudit(a.data);
      setReadinessRefresh((current) => current + 1);
      setVersionRefresh((current) => current + 1);
    } catch (error) {
      setProjectLoadError(true);
      throw error;
    }
  }, [pid]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  useEffect(() => {
    if (!project) return;
    if (devices.length && !devices.some((device) => device.id === requestedDeviceId)) {
      const preferred = devices.find((device) => device.id === project.device_id) || devices[0];
      if (preferred) {
        setSearchParams((current) => {
          const next = new URLSearchParams(current);
          next.set("device_id", preferred.id);
          return next;
        }, { replace: true });
      }
      return;
    }
    let cancelled = false;
    const params = activeDeviceId ? { device_id: activeDeviceId } : {};
    setActiveLoading(true);
    setActiveLoadError(false);
    api.get(`/projects/${pid}/versions`, { params })
      .then(({ data }) => {
        if (!cancelled) setActive(data?.[0] || null);
      })
      .catch(() => {
        if (!cancelled) {
          setActive(null);
          setActiveLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setActiveLoading(false);
      });
    return () => { cancelled = true; };
  }, [project, devices, requestedDeviceId, activeDeviceId, pid, versionRefresh, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setProfileLoading(true);
    setProfileError(false);
    api.get("/user/profile")
      .then(({ data }) => {
        if (!cancelled) setCompanyProfile(data);
      })
      .catch(() => {
        if (!cancelled) setProfileError(true);
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const deviceIds = devices.map((device) => device.id).filter(Boolean).join(",");
  useEffect(() => {
    let cancelled = false;
    if (!pid || !deviceIds) {
      setAttachmentsByDevice({});
      setAttachmentsLoading(false);
      setAttachmentsError(false);
      return undefined;
    }
    setAttachmentsLoading(true);
    setAttachmentsError(false);
    const ids = deviceIds.split(",");
    Promise.all(ids.map(async (deviceId) => {
      const { data } = await api.get(
        `/projects/${pid}/devices/${deviceId}/regulatory-documents`,
        { params: { module: "ce_marking" } },
      );
      return [deviceId, data || []];
    }))
      .then((records) => {
        if (!cancelled) setAttachmentsByDevice(Object.fromEntries(records));
      })
      .catch(() => {
        if (!cancelled) setAttachmentsError(true);
      })
      .finally(() => {
        if (!cancelled) setAttachmentsLoading(false);
      });
    return () => { cancelled = true; };
  }, [pid, deviceIds, readinessRefresh]);

  async function rerun() {
    if (!active || applicableRole !== "manufacturer") return;
    if (active.frozen_at) {
      if (!window.confirm(t("pd.confirm_rerun"))) return;
    }
    setReanalyzing(true);
    setRerunNotice("");
    setRerunError("");
    try {
      const payload = { ...active.intake, lang };
      const { data } = await api.post(`/projects/${pid}/analyze`, payload, {
        params: activeDeviceId ? { device_id: activeDeviceId } : {},
      });
      const version = data?.version;
      const suggestedClass = data?.classification?.suggested_class;
      let notice;
      if (data?.llm?.error) {
        notice = t("pd.rerun_ai_unavailable")
          .replace("{version}", version ?? "—")
          .replace("{class}", suggestedClass || t("pd.rerun_no_class_value"));
      } else if (suggestedClass && suggestedClass === active.classification?.suggested_class) {
        notice = t("pd.rerun_same")
          .replace("{version}", version ?? "—")
          .replace("{class}", suggestedClass);
      } else if (suggestedClass) {
        notice = t("pd.rerun_changed")
          .replace("{version}", version ?? "—")
          .replace("{class}", suggestedClass);
      } else {
        notice = t("pd.rerun_no_class")
          .replace("{version}", version ?? "—");
      }
      setRerunNotice(notice);
      try {
        await load();
      } catch {
        setRerunNotice(t("pd.rerun_saved_refresh_failed").replace("{version}", version ?? "—"));
      }
    } catch {
      setRerunError(t("pd.rerun_failed"));
    } finally { setReanalyzing(false); }
  }

  async function reevaluateClinical(clinical_context) {
    if (!active) return false;
    if (active.frozen_at && !window.confirm(t("pd.confirm_rerun"))) return false;
    setReanalyzing(true);
    try {
      await api.post(
        `/projects/${pid}/versions/${active.version}/clinical/re-evaluate`,
        { clinical_context },
        { params: activeDeviceId ? { device_id: activeDeviceId } : {} },
      );
      await load();
      return true;
    } finally { setReanalyzing(false); }
  }

  function selectProjectModule(moduleValue, sectionOverride) {
    const nextSection = sectionOverride || getProjectSectionForModule(moduleValue);
    setActiveProjectModule(moduleValue || "");
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("section", nextSection);
      next.set("module", moduleValue || "none");
      return next;
    });
  }

  function scrollToProjectTarget(targetId, behavior = "smooth") {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.getElementById(targetId)?.scrollIntoView({ behavior, block: "start" });
      });
    });
  }

  function openRegulatoryTab(tab) {
    selectProjectModule(tab);
    scrollToProjectTarget(getRegulatoryTargetId(tab));
  }

  async function downloadResponse(route, params, filename, id) {
    setExportError("");
    setDownloadBusyId(id || "export");
    try {
      const { data } = await api.get(route, {
        params: { ...(params || {}), ...(activeDeviceId ? { device_id: activeDeviceId } : {}) },
        responseType: "blob",
      });
      downloadBlob(data, filename);
    } catch (error) {
      const responseData = error.response?.data;
      let detail = responseData?.detail;
      if (responseData instanceof Blob) {
        try {
          const parsed = JSON.parse(await responseData.text());
          detail = parsed?.detail;
        } catch {
          detail = "";
        }
      }
      setExportError(formatApiError(detail));
    } finally {
      setDownloadBusyId("");
    }
  }

  async function downloadReadinessItem(item) {
    if (!active) return;
    const langParam = { lang };
    let route = "";
    let params = langParam;
    let filename = "mdrflow-document.pdf";
    if (item.type === "document") {
      route = `/projects/${pid}/versions/${active.version}/documents/${item.documentType}/pdf`;
      filename = `mdrflow-${item.documentType}-${pid.slice(0, 8)}-v${active.version}.pdf`;
    } else if (item.type === "gspr") {
      route = `/projects/${pid}/versions/${active.version}/gspr/pdf`;
      filename = `mdrflow-GSPR-${pid.slice(0, 8)}-v${active.version}.pdf`;
    } else if (item.type === "udi") {
      route = `/projects/${pid}/devices/${item.deviceId}/exports/udi.pdf`;
      filename = `mdrflow-UDI-${item.deviceId.slice(0, 8)}.pdf`;
    } else if (item.type === "operator_evidence") {
      route = `/projects/${pid}/devices/${item.deviceId}/exports/operator-evidence.pdf`;
      params = { ...langParam, role_view: item.roleView };
      filename = `mdrflow-evidence-${item.deviceId.slice(0, 8)}.pdf`;
    } else if (item.type === "attachment") {
      route = `/projects/${pid}/devices/${item.deviceId}/regulatory-documents/${item.attachmentId}/download`;
      params = {};
      filename = item.fileName || `mdrflow-attachment-${item.attachmentId.slice(0, 8)}`;
    }
    if (route) await downloadResponse(route, params, filename, item.id || item.type);
  }

  async function downloadPackage() {
    if (!active) return;
    setPackageBusy(true);
    setPackageError("");
    try {
      const { data } = await api.get(
        `/projects/${pid}/versions/${active.version}/documents/pre-registration-package.zip`,
        {
          params: {
            lang,
            role_view: applicableRole || "",
            ...(activeDeviceId ? { device_id: activeDeviceId } : {}),
          },
          responseType: "blob",
        },
      );
      downloadBlob(data, `mdrflow-documentacion-${pid.slice(0, 8)}-v${active.version}.zip`);
    } catch (error) {
      const responseData = error.response?.data;
      let detail = responseData?.detail;
      if (responseData instanceof Blob) {
        try {
          const parsed = JSON.parse(await responseData.text());
          detail = parsed?.detail;
        } catch {
          detail = "";
        }
      }
      setPackageError(formatApiError(detail));
    } finally {
      setPackageBusy(false);
    }
  }

  function openPmcfPlan() {
    setClinicalFocusRequest("pmcf_plan");
    selectProjectModule("clinical");
    scrollToProjectTarget(getRegulatoryTargetId("clinical"));
  }

  function openReadinessGaps() {
    if (!applicableRole) {
      scrollToProjectTarget("project-role-control");
      return;
    }
    if (applicableRole === "manufacturer") {
      selectProjectModule("capa");
      scrollToProjectTarget("postmarket-capa");
      return;
    }
    openRegulatoryTab("operator-review");
  }

  const readinessItems = buildEudamedReadinessItems({
    t,
    roleView: applicableRole,
    profile: companyProfile,
    profileLoading,
    profileError,
    devices: activeDevice ? [activeDevice] : [],
    version: active,
    attachmentsByDevice,
    attachmentsLoading,
    attachmentsError,
    onDownload: downloadReadinessItem,
    onOpenCompany: () => nav("/company"),
    onOpenDevice: (deviceId) => nav(deviceId ? `/devices/${deviceId}` : `/projects/${pid}/devices/new`),
    onOpenTab: openRegulatoryTab,
    onOpenProjectRole: () => {
      scrollToProjectTarget("project-role-control");
    },
  });
  const readinessRecordedCount = readinessItems.filter((item) => (
    item.status === "recorded" || item.status === "review"
  )).length;
  const readinessSummary = readinessItems.length
    ? t("eudamed.summary")
      .replace("{recorded}", String(readinessRecordedCount))
      .replace("{total}", String(readinessItems.length))
    : t("eudamed.summary_empty");
  const readinessLabels = {
    eyebrow: t("eudamed.eyebrow"),
    title: t("eudamed.title"),
    roleViewAria: t("eudamed.role_view_aria"),
    roleViewNote: t("eudamed.role_view_note"),
    statusScopeNote: t("eudamed.status_scope"),
    recordsHeading: t("eudamed.records_heading"),
    documentsAction: t("eudamed.documents_action"),
    gapsAction: t("eudamed.gaps_action"),
    listAria: t("eudamed.list_aria"),
    emptyText: t("eudamed.empty"),
    packageTitle: t("eudamed.package_title"),
    packageDescription: t("eudamed.package_description"),
    packageBusy: t("eudamed.package_busy"),
    downloadPackage: t("eudamed.package_download"),
    downloading: t("reg.downloading"),
  };

  useEffect(() => {
    const normalizedSection = getProjectSection(sectionParam, moduleParam);
    const normalizedModule = getProjectModule(sectionParam, moduleParam);
    if (sectionParam !== normalizedSection || moduleParam !== (normalizedModule || "none")) {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set("section", normalizedSection);
        next.set("module", normalizedModule || "none");
        return next;
      }, { replace: true });
    }
  }, [project, sectionParam, moduleParam, setSearchParams]);

  useEffect(() => {
    if (section === "overview") {
      if (activeProjectModule) setActiveProjectModule("");
      return;
    }
    if (!applicableRole || !activeProjectModule) return;
    const currentSectionModules = visibleProjectSectionTabs[section] || [];
    if (currentSectionModules.includes(activeProjectModule)) return;
    if (section === "classification") {
      setActiveProjectModule("");
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set("module", "none");
        return next;
      }, { replace: true });
      return;
    }
    const defaultModule = section === "documents"
      ? roleWorkspace.defaultRegulatoryTab
      : roleWorkspace.defaultPostmarketTab;
    const nextModule = currentSectionModules.includes(defaultModule)
      ? defaultModule
      : currentSectionModules[0] || "";
    setActiveProjectModule(nextModule);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("section", section);
      next.set("module", nextModule || "none");
      return next;
    }, { replace: true });
  }, [applicableRole, visibleProjectSectionTabs, activeProjectModule, section, roleWorkspace.defaultRegulatoryTab, roleWorkspace.defaultPostmarketTab, setSearchParams]);

  useEffect(() => {
    const moduleFromUrl = getProjectModule(sectionParam, moduleParam);
    if (moduleFromUrl !== activeProjectModule) setActiveProjectModule(moduleFromUrl);
  }, [sectionParam, moduleParam, activeProjectModule]);

  if (!project) return (
    <AppShell>
      {projectLoadError ? (
        <div role="alert" className="grid-card space-y-3 p-6 text-sm" data-testid="project-load-error">
          <p>{t("workspace.load_error")}</p>
          <button type="button" onClick={() => load().catch(() => {})} className="btn-primary px-4 py-2">{t("app.retry")}</button>
        </div>
      ) : <WorkspaceLoading message={t("workspace.loading_project")} />}
    </AppShell>
  );

  const regulatoryContent = {
    preparation: (
      <div className="space-y-6">
        {applicableRole === "manufacturer" ? <PRRCWidget project={project} onChange={load} /> : null}
        <EudamedReadinessPanel
          projectName={project.name}
          roleLabel={t("eudamed.role_label")}
          roleView={applicableRole ? t(roleWorkspace.labelKey) : t("project.role_pending")}
          items={readinessItems}
          summaryText={readinessSummary}
          disclaimer={`${t("eudamed.disclaimer")} ${t("eudamed.evidence_note")}`}
          labels={readinessLabels}
          onDownloadPackage={active && applicableRole ? downloadPackage : undefined}
          packageBusy={packageBusy}
          packageError={packageError}
          exportError={exportError}
          downloadBusyId={downloadBusyId}
          hideGeneratedDocumentDownloads
          onJumpToDocuments={() => {
            selectProjectModule("documents");
            scrollToProjectTarget("module-documents", "auto");
          }}
          onJumpToGaps={openReadinessGaps}
          onOpenProjectRole={() => nav("/company")}
        />
      </div>
    ),
    classification: (
      <ClassificationTab
        version={active}
        device={activeDevice}
        onEditAssessment={() => nav(`/projects/new?project_id=${encodeURIComponent(pid)}&device_id=${encodeURIComponent(activeDeviceId)}`)}
      />
    ),
    "operator-review": activeDevice && applicableRole
      ? <OperatorClassificationReview project={project} device={activeDevice} role={applicableRole} onSaved={load} />
      : roleSetupPrompt(),
    risk: <RiskTab project={project} version={active} deviceId={activeDeviceId} onChange={load} onOpenAiAct={() => openRegulatoryTab("aiact")} />,
    gspr: <GsprTab project={project} version={active} deviceId={activeDeviceId} onChange={load} />,
    "notified-body": <NotifiedBodyTab project={project} devices={activeDevice ? [activeDevice] : []} onChange={load} />,
    "ce-marking": <CeMarkingTab project={project} devices={activeDevice ? [activeDevice] : []} version={active} deviceId={activeDeviceId} role={applicableRole || "manufacturer"} onChange={load} onEditCompany={() => nav("/company")} />,
    techfile: <TechnicalFileTab project={project} version={active} />,
    clinical: (
      <ClinicalTab
        version={active}
        onReevaluate={reevaluateClinical}
        focusFieldOnOpen={clinicalFocusRequest}
        onFocusHandled={clearClinicalFocusRequest}
      />
    ),
    aiact: <AiActTab version={active} />,
    submission: <SubmissionTab project={project} version={active} deviceId={activeDeviceId} onChange={load} />,
    documents: active
      ? <DocumentsSection project={project} version={active} deviceId={activeDeviceId} roleView={applicableRole || "manufacturer"} lang={lang} onChange={load} showPackageAction={Boolean(applicableRole)} canManageDocuments={canManageGeneratedDocuments(applicableRole)} />
      : (
        <div className="space-y-3" data-testid="documents-no-active-version">
          <Empty msg={t("pd.documents.no_active_version")} />
          {applicableRole && activeDevice ? (
            <Link to={`/projects/new?project_id=${encodeURIComponent(pid)}&device_id=${encodeURIComponent(activeDevice.id)}`} className="btn-primary inline-flex px-3 py-2 text-sm" data-testid="documents-start-assessment">
              {t("pd.start_assessment")}
            </Link>
          ) : null}
        </div>
      ),
  };

  const postmarketContent = {
    pms: <PmsTab project={project} version={active} devices={activeDevice ? [activeDevice] : []} deviceId={activeDeviceId} />,
    pmcf: (
      <div className="grid-card max-w-3xl space-y-3 p-5" data-testid="pmcf-access">
        <h2 className="text-base font-bold">{t("clinical.pmcf_title")}</h2>
        <p className="text-sm leading-relaxed text-[var(--text-2)]">{t("clinical.pmcf_sub")}</p>
        <div>
          <button type="button" onClick={openPmcfPlan} className="btn-primary px-3 py-2 text-sm" data-testid="pmcf-open-plan">
            {t("clinical.open_pmcf")}
          </button>
        </div>
      </div>
    ),
    capa: (
      <div className="space-y-5" data-testid="postmarket-capa">
        <section>
          <h3 className="mb-3 text-base font-bold">{t("qms.capa.title")}</h3>
          <QmsTab project={project} type="capa" deviceId={activeDeviceId} />
        </section>
        <section className="border-t border-[var(--border)] pt-5" id="compliance-gap-analysis" data-testid="compliance-gap-analysis">
          <h3 className="mb-3 text-base font-bold">{t("qms.capa.gaps_title")}</h3>
          <GapsTab version={active} project={project} deviceId={activeDeviceId} onChange={load} />
        </section>
      </div>
    ),
    ncr: <QmsTab project={project} type="ncr" deviceId={activeDeviceId} />,
    complaints: <QmsTab project={project} type="complaint" deviceId={activeDeviceId} />,
    recall: <QmsTab project={project} type="recall" deviceId={activeDeviceId} />,
    fsca: <QmsTab project={project} type="fsca" deviceId={activeDeviceId} />,
    trace: <TraceTab project={project} role={applicableRole || "manufacturer"} deviceId={activeDeviceId} device={activeDevice} />,
    audit: <AuditTab logs={audit} />,
  };
  const projectToolContent = { ...regulatoryContent, ...postmarketContent };
  const projectSectionTitle = {
    overview: "pd.summary.title",
    classification: "pd.section.classification",
    documents: "pd.section.documents",
    traceability: "pd.section.traceability",
  }[section];

  const projectDevicesSection = (
    <section className="grid-card p-4 sm:p-5" data-testid="project-devices">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--text-2)]">
            <Cpu size={14} /> {t("pd.devices")}
          </div>
          <div className="mt-1 text-sm text-[var(--text-2)]">
            {devices.length} {t("dash.device_count")}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {devices.length > 0 && (
            <button
              type="button"
              aria-expanded={deviceListExpanded}
              onClick={() => setDeviceListExpanded((expanded) => !expanded)}
              data-testid="project-devices-toggle"
              className="rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold text-[var(--text)] hover:border-[var(--accent)]"
            >
              {deviceListExpanded ? t("pd.devices.hide") : t("pd.devices.show")}
            </button>
          )}
          <Link to={`/projects/${pid}/devices/new`} data-testid="project-add-device" className="inline-flex items-center justify-center gap-2 rounded-sm border border-[var(--border)] bg-[var(--surface)] px-4 py-2 text-sm font-semibold text-[var(--text)] hover:border-[var(--accent)]">
            <Plus size={14} weight="bold" /> {t("pd.add_device")}
          </Link>
        </div>
      </div>
      {devices.length === 0 ? (
        <div className="rounded-sm border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--text-2)]">
          {t("pd.no_devices")}
        </div>
      ) : deviceListExpanded ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {devices.map((device) => (
            <Link key={device.id} to={`/devices/${device.id}`} data-testid={`project-device-${device.id}`} className="min-w-0 rounded-sm border border-[var(--border)] p-4 transition-all hover:border-[var(--accent)] hover:shadow-sm">
              <div className="break-words font-semibold text-[var(--text)]">{device.name}</div>
              <div className="mt-1 break-words text-xs text-[var(--text-2)]">{device.model || "—"}</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {device.mdr_class && <span className={`chip chip-${device.mdr_class}`}>{device.mdr_class}</span>}
                {device.regulatory_status && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-2)]">{t(`dev.st.${device.regulatory_status}`)}</span>
                )}
                {device.basic_udi_di && <span className="text-[10px] font-mono text-[var(--text-2)]">Basic UDI-DI ✓</span>}
              </div>
            </Link>
          ))}
        </div>
      ) : null}
    </section>
  );

  const startProductAssessmentAction = !active && activeDevice ? (
    <div className="flex justify-end">
      <Link to={`/projects/new?project_id=${encodeURIComponent(pid)}&device_id=${encodeURIComponent(activeDevice.id)}`} className="btn-primary inline-flex px-3 py-2 text-sm" data-testid="start-product-assessment">
        {t("pd.start_assessment")}
      </Link>
    </div>
  ) : null;

  const projectContextSection = (
    <details className="mb-4 rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)]" data-testid="project-context-settings">
      <summary className="cursor-pointer list-inside px-4 py-3 text-sm font-semibold text-[var(--text)]">
        {t("pd.project_context")}
      </summary>
      <div className="space-y-3 border-t border-[var(--border)] p-3 sm:p-4">
        {projectDevicesSection}
      </div>
    </details>
  );

  function openProjectOverview() {
    setActiveProjectModule("");
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("section", "overview");
      next.set("module", "none");
      if (activeDeviceId) next.set("device_id", activeDeviceId);
      return next;
    });
  }

  const renderProjectModules = (moduleValues) => (
    <div className="space-y-3">
      {moduleValues.map((value) => {
        const tab = REGULATORY_TABS.find((item) => item.value === value)
          || POSTMARKET_TABS.find((item) => item.value === value);
        return (
          <ProjectModule
            key={value}
            id={value === "capa" ? "postmarket-capa" : `module-${value}`}
            title={t(tab?.label || value)}
            expanded={activeProjectModule === value}
            onToggle={() => selectProjectModule(activeProjectModule === value ? "" : value, section)}
          >
            {projectToolContent[value]}
          </ProjectModule>
        );
      })}
    </div>
  );

  return (
    <AppShell assistantProject={project} assistantVersion={active}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={() => nav("/dashboard")} className="inline-flex items-center gap-1 text-xs font-mono text-zinc-500 hover:text-blue-700 transition-colors" data-testid="back-dashboard">
          <CaretLeft size={12} /> {t("pd.back")}
        </button>
        {section !== "overview" ? (
          <button type="button" onClick={openProjectOverview} className="text-xs font-semibold text-[var(--accent-text)] underline underline-offset-2" data-testid="project-summary-back">
            {t("pd.summary.back")}
          </button>
        ) : null}
      </div>
      <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{t("pd.project")} · {project.id.slice(0, 8)}</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight break-words" data-testid="project-name">{project.name}</h1>
          {activeDevice && (
            <p className="mt-1 break-words text-sm text-[var(--text-2)]" data-testid="active-product-context">
              {t("pd.active_product")}: {activeDevice.name}{activeDevice.model ? ` · ${activeDevice.model}` : ""}
            </p>
          )}
        </div>
        <div className="flex w-full items-center gap-3 sm:w-auto">
          {devices.length > 1 && (
            <label className="min-w-0 flex-1 sm:flex-none">
              <span className="sr-only">Producto activo</span>
              <select
                value={activeDeviceId}
                onChange={(event) => setSearchParams((current) => {
                  const next = new URLSearchParams(current);
                  next.set("device_id", event.target.value);
                  return next;
                })}
                className="w-full max-w-64 rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text)]"
                data-testid="active-product-selector"
              >
                {devices.map((device) => <option key={device.id} value={device.id}>{device.name}{device.model ? ` · ${device.model}` : ""}</option>)}
              </select>
            </label>
          )}
        </div>
      </div>

      {projectContextSection}

      <main data-testid="project-sections" data-active-section={section} className="w-full">
        <h2 className="mb-4 border-b border-[var(--border)] pb-3 text-base font-bold text-[var(--text)] sm:text-lg" data-testid="project-section-heading">
          {t(projectSectionTitle)}
        </h2>

        {section === "overview" && (
          <ProjectOverview
            project={project}
            device={activeDevice}
            version={active}
            versionLoading={activeLoading}
            versionError={activeLoadError}
            role={applicableRole}
            readinessItems={readinessItems}
          />
        )}

        {section === "classification" && <div className="space-y-4" data-testid="project-content-classification">
          {startProductAssessmentAction}
          {active && applicableRole === "manufacturer" && (
            <div className="flex justify-end">
              <button data-testid="rerun-btn" disabled={reanalyzing} onClick={() => rerun()} className="btn-primary inline-flex items-center gap-1 px-3 py-2 text-xs">
                <ArrowsClockwise size={14} weight="bold" /> {reanalyzing ? t("pd.running") : t("pd.rerun")}
              </button>
              {(rerunNotice || rerunError) && (
                <div
                  role={rerunError ? "alert" : "status"}
                  data-testid={rerunError ? "rerun-error" : "rerun-status"}
                  className="mt-2 max-w-xl rounded-sm border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-xs text-[var(--text)]"
                >
                  {rerunError || rerunNotice}
                </div>
              )}
            </div>
          )}
          {!applicableRole ? (
            roleSetupPrompt()
          ) : visibleProjectSectionTabs.classification.length ? (
            projectToolContent.classification
          ) : (
            <Empty msg={t("pd.section.role_unavailable")} />
          )}
        </div>}

        {section === "documents" && <div className="space-y-4" data-testid="project-content-documents">
          {active?.frozen_at && (
            <div className="grid-card flex items-center gap-3 border-l-4 border-l-emerald-500 p-3" data-testid="documents-frozen-banner">
              <Lock size={16} weight="fill" className="text-emerald-600" />
              <div className="break-words text-xs font-mono text-[var(--text-2)]">
                <span className="font-bold">{t("pd.frozen_banner")}</span> {active.frozen_by_email} · {active.frozen_at} · SHA-256 {active.signature_hash?.slice(0, 16)}…
              </div>
            </div>
          )}
          {!applicableRole ? (
            roleSetupPrompt()
          ) : (
            renderProjectModules(visibleProjectSectionTabs.documents)
          )}
        </div>}

        {section === "traceability" && <div className="space-y-4" data-testid="project-content-traceability">
          {!applicableRole ? (
            roleSetupPrompt()
          ) : (
            <>
              <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{t("pd.postmarket.groups")}</div>
              {renderProjectModules(visibleProjectSectionTabs.traceability)}
            </>
          )}
        </div>}
      </main>

    </AppShell>
  );
}

function ProjectModule({ id, title, expanded, onToggle, children }) {
  const t = useT();
  return (
    <section id={id} className="overflow-hidden rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)]" data-testid={`module-${id}`}>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[var(--surface)]"
      >
        <span className="min-w-0 break-words text-sm font-bold text-[var(--text)]">{title}</span>
        <span className="shrink-0 text-xs font-semibold text-[var(--accent-text)]">
          {expanded ? t("pd.module.close") : t("pd.module.open")}
        </span>
      </button>
      {expanded && <div className="border-t border-[var(--border)] p-3 sm:p-4">{children}</div>}
    </section>
  );
}
